import {
  Box,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TextField,
  MenuItem,
  Select,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid2,
  IconButton,
  FormControl,
  InputLabel,
  useMediaQuery,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import React, { useState } from "react";
import { Field, useField, useForm } from "react-final-form";
import ClearIcon from "@mui/icons-material/Clear";
import CopyIcon from "@mui/icons-material/ContentCopy";
import { FieldArray } from "react-final-form-arrays";
import History from "../ClientPortal/History";
import VideocamIcon from "@mui/icons-material/Videocam";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const EXERCISE_TYPES = {
  SINGLE: "single",
  CIRCUIT: "circuit",
};

const FIELD_OPTIONS = ["WEIGHT", "RPE", "TIME", "DISTANCE"];

const DISTANCE_UNITS = [
  { value: "m", label: "m" },
  { value: "ft", label: "ft" },
  { value: "km", label: "km" },
  { value: "mi", label: "mi" },
  { value: "yd", label: "yd" },
];

// Exercise-level fields (max 2). Empty array = intentional. Old data has type on sets.
const getExerciseFields = (exercise) => {
  if (Array.isArray(exercise?.fields)) {
    return exercise.fields.slice(0, 2);
  }
  const firstSet = exercise?.sets?.[0];
  if (firstSet && (firstSet.type != null || (Array.isArray(firstSet.types) && firstSet.types.length > 0))) {
    if (Array.isArray(firstSet.types) && firstSet.types.length > 0) return firstSet.types.slice(0, 2);
    if (firstSet.type === "TIME") return ["TIME"];
    if (firstSet.type === "RPE") return ["RPE", "WEIGHT"];
    return ["WEIGHT", "RPE"];
  }
  return ["RPE", "WEIGHT"];
};

const AddExercise = ({
  index,
  removeExercise,
  inline = false,
  onOpenVideoModal,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
}) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const form = useForm();

  // Get the current video value for this exercise using useField
  const { input: videoInput } = useField(`exercises[${index}].video`, {
    subscription: { value: true },
  });
  const currentVideoUrl = videoInput.value;

  const defaultSet = {
    reps: "",
    weight: "",
    rpe: "",
    time: "",
    distance: "",
    weightPerSide: false,
    repsPerSide: false,
  };

  const { input: fieldsInput } = useField(`exercises[${index}].fields`, {
    subscription: { value: true },
  });
  const exerciseFields =
    Array.isArray(fieldsInput.value) && fieldsInput.value.length > 0
      ? fieldsInput.value.slice(0, 2)
      : getExerciseFields(form.getState().values?.exercises?.[index] || {});

  return (
    <Box>
      <Grid2
        display="flex"
        sx={{ mb: 2 }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            aria-label="Move exercise up"
            disabled={!canMoveUp}
            onClick={onMoveUp}
          >
            <ArrowUpwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Move exercise down"
            disabled={!canMoveDown}
            onClick={onMoveDown}
          >
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
          <Typography sx={{ ml: 1 }}>Exercise {index + 1}</Typography>
        </Box>
        <ClearIcon color={"warning"} onClick={removeExercise} />
      </Grid2>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Field
          name={`exercises[${index}].name`}
          render={({ input }) => (
            <TextField
              {...input}
              label="Exercise Name"
              size="small"
              fullWidth
            />
          )}
        />
        <IconButton
          color="primary"
          aria-label="add video"
          onClick={() => onOpenVideoModal(index)}
          size="small"
        >
          <VideocamIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Field
          name={`exercises[${index}].restTime`}
          render={({ input }) => (
            <TextField
              {...input}
              label="Rest Time (seconds)"
              type="number"
              size="small"
              sx={{ width: 200 }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onWheel={(e) => e.target.blur()} // prevent scroll from changing value
            />
          )}
        />
        <Field
          name={`exercises[${index}].weightUnit`}
          render={({ input }) => (
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Weight Unit</InputLabel>
              <Select {...input} label="Weight Unit" defaultValue="lbs">
                <MenuItem value="lbs">Pounds (lbs)</MenuItem>
                <MenuItem value="kg">Kilograms (kg)</MenuItem>
              </Select>
            </FormControl>
          )}
        />
        <Field
          name={`exercises[${index}].exerciseType`}
          subscription={{ value: true }}
          render={({ input: typeInput }) => {
            const exerciseType = typeInput.value ?? EXERCISE_TYPES.SINGLE;
            return (
            <>
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Exercise Type</InputLabel>
                <Select
                  {...typeInput}
                  value={exerciseType}
                  label="Exercise Type"
                  onChange={(e) => typeInput.onChange(e.target.value)}
                >
                  <MenuItem value={EXERCISE_TYPES.SINGLE}>Single</MenuItem>
                  <MenuItem value={EXERCISE_TYPES.CIRCUIT}>Circuit</MenuItem>
                </Select>
              </FormControl>
              {exerciseType !== EXERCISE_TYPES.CIRCUIT && (
                <Field
                  name={`exercises[${index}].fields`}
                  subscription={{ value: true }}
                  render={({ input }) => {
                    const selected =
                      Array.isArray(input.value) && input.value.length > 0
                        ? input.value
                        : exerciseFields;
                    return (
                      <FormControl size="small" sx={{ width: 180 }}>
                        <InputLabel>Fields (max 2)</InputLabel>
                        <Select
                          multiple
                          {...input}
                          value={selected}
                          label="Fields (max 2)"
                          onChange={(e) => {
                            const next = Array.from(
                              new Set(e.target.value)
                            ).slice(0, 2);
                            input.onChange(next);
                          }}
                          renderValue={(vals) => (vals || []).join(", ")}
                        >
                          {FIELD_OPTIONS.map((option) => (
                            <MenuItem
                              key={option}
                              value={option}
                              disabled={
                                !selected.includes(option) &&
                                selected.length >= 2
                              }
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }}
                />
              )}
              {exerciseType !== EXERCISE_TYPES.CIRCUIT &&
                (Array.isArray(fieldsInput.value) ? fieldsInput.value : exerciseFields).includes("DISTANCE") && (
                  <Field
                    name={`exercises[${index}].distanceUnit`}
                    subscription={{ value: true }}
                    render={({ input }) => (
                      <FormControl size="small" sx={{ width: 120 }}>
                        <InputLabel>Distance unit</InputLabel>
                        <Select
                          {...input}
                          value={input.value || "m"}
                          label="Distance unit"
                        >
                          {DISTANCE_UNITS.map((u) => (
                            <MenuItem key={u.value} value={u.value}>
                              {u.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                )}
            </>
            );
          }}
        />
      </Box>
      {currentVideoUrl && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block", mb: 1 }}
          >
            Video: {currentVideoUrl.split("/").pop()}
          </Typography>
          <IconButton
            color="error"
            aria-label="remove video"
            onClick={() => {
              videoInput.onChange(""); // Clear the video field
            }}
            size="small"
          >
            <ClearIcon />
          </IconButton>
        </Box>
      )}
      <Field
        subscription={{ value: true }}
        name={`exercises[${index}].description`}
        render={({ input }) => (
          <TextField
            fullWidth
            multiline
            size="small"
            sx={{ mt: 2 }}
            label="Exercise description"
            value={input.value}
            onChange={input.onChange}
          />
        )}
      />
      <Field
        name={`exercises[${index}].exerciseType`}
        subscription={{ value: true }}
        render={({ input: typeInput }) => {
          const isCircuit = typeInput.value === EXERCISE_TYPES.CIRCUIT;
          return (
            <>
              {isCircuit ? null : (
                <FieldArray
                  name={`exercises[${index}].sets`}
                  render={({ fields }) => {
                    const activeFields = exerciseFields;

                    return (
                      <>
                        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                          <Button
                            size={inline ? "small" : "medium"}
                            onClick={() => fields.push(defaultSet)}
                          >
                            Add Set
                          </Button>
                          {fields.length > 0 && (
                            <Button
                              size={inline ? "small" : "medium"}
                              onClick={() => {
                                const lastSet = fields.value[fields.length - 1];
                                fields.push({
                                  ...lastSet,
                                });
                              }}
                            >
                              Copy Last Set
                            </Button>
                          )}
                        </Box>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {!isMobile && (
                                <TableCell sx={{ padding: "5px" }}>#</TableCell>
                              )}
                              <TableCell>Reps (each)</TableCell>
                              {activeFields.includes("WEIGHT") && (
                                <TableCell>Weight (each)</TableCell>
                              )}
                              {activeFields.includes("RPE") && (
                                <TableCell>RPE</TableCell>
                              )}
                              {activeFields.includes("TIME") && (
                                <TableCell>Time (sec)</TableCell>
                              )}
                              {activeFields.includes("DISTANCE") && (
                                <TableCell>Distance</TableCell>
                              )}
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fields.map((name, i) => (
                              <SetActions
                                key={name}
                                exerciseIndex={index}
                                setIndex={i}
                                onRemove={() => fields.remove(i)}
                                fields={fields}
                                activeFields={activeFields}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    );
                  }}
                />
              )}

              {isCircuit && (
                <FieldArray
                  name={`exercises[${index}].circuit`}
                  render={({ fields: roundFields }) => {
                    // Deep clone the last round's exercises
                    const cloneRound = (round) => {
                      if (!Array.isArray(round) || round.length === 0) {
                        return [];
                      }
                      return round.map((ex) => ({
                        name: ex.name || "",
                        description: ex.description || "",
                        video: ex.video || "",
                        fields: (Array.isArray(ex.fields)
                          ? ex.fields
                          : ["RPE", "WEIGHT"]
                        ).slice(0, 2),
                        distanceUnit: ex.distanceUnit || "m",
                        sets: (ex.sets || []).map((set) => ({
                          reps: set.reps ?? "",
                          weight: set.weight ?? "",
                          rpe: set.rpe ?? "",
                          time: set.time ?? "",
                          distance: set.distance ?? "",
                          weightPerSide: set.weightPerSide ?? false,
                          repsPerSide: set.repsPerSide ?? false,
                        })),
                      }));
                    };

                    const handleAddRound = () => {
                      const circuitValues = roundFields.value || [];
                      const lastRound =
                        circuitValues.length > 0
                          ? circuitValues[circuitValues.length - 1]
                          : [];

                      // If no rounds exist, create an empty round
                      if (circuitValues.length === 0) {
                        roundFields.push([]);
                      } else {
                        // Copy the last round
                        roundFields.push(cloneRound(lastRound));
                      }
                    };

                    // Ensure at least one round exists
                    if (roundFields.length === 0) {
                      roundFields.push([]);
                    }

                    return (
                      <Box sx={{ mt: 3 }}>
                        {roundFields.map((roundName, roundIndex) => (
                          <Card
                            key={roundName}
                            variant="outlined"
                            sx={{ mb: 2 }}
                          >
                            <CardHeader
                              title={`Round ${roundIndex + 1}`}
                              action={
                                roundFields.length > 1 ? (
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                      roundFields.remove(roundIndex)
                                    }
                                    aria-label="remove round"
                                  >
                                    <ClearIcon />
                                  </IconButton>
                                ) : null
                              }
                              sx={{ pb: 0 }}
                            />
                            <CardContent sx={{ pt: 1 }}>
                              <FieldArray
                                name={roundName}
                                render={({ fields: circuitExerciseFields }) => {
                                  const addExercise = () =>
                                    circuitExerciseFields.push({
                                      name: "",
                                      description: "",
                                      video: "",
                                      fields: ["RPE", "WEIGHT"],
                                      distanceUnit: "m",
                                      sets: [
                                        {
                                          reps: "",
                                          weight: "",
                                          rpe: "",
                                          time: "",
                                          distance: "",
                                          weightPerSide: false,
                                          repsPerSide: false,
                                        },
                                      ],
                                    });
                                  return (
                                    <>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {circuitExerciseFields.length === 0
                                            ? "No circuit exercises yet."
                                            : `${
                                                circuitExerciseFields.length
                                              } exercise${
                                                circuitExerciseFields.length !==
                                                1
                                                  ? "s"
                                                  : ""
                                              }`}
                                        </Typography>
                                        <Button
                                          size="small"
                                          onClick={addExercise}
                                        >
                                          Add Exercise
                                        </Button>
                                      </Box>
                                      {circuitExerciseFields.map(
                                        (subName, subIndex) => (
                                          <Box
                                            key={subName}
                                            sx={{
                                              borderBottom:
                                                subIndex ===
                                                circuitExerciseFields.length - 1
                                                  ? "none"
                                                  : "1px solid #e0e0e0",
                                              pb: 1.5,
                                              mb: 1.5,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <Field
                                                name={`${subName}.name`}
                                                render={({ input }) => (
                                                  <TextField
                                                    {...input}
                                                    label={`Circuit Exercise ${
                                                      subIndex + 1
                                                    }`}
                                                    size="small"
                                                    fullWidth
                                                  />
                                                )}
                                              />
                                              <IconButton
                                                color="primary"
                                                aria-label="add video"
                                                onClick={() =>
                                                  onOpenVideoModal(
                                                    index,
                                                    roundIndex,
                                                    subIndex
                                                  )
                                                }
                                                size="small"
                                              >
                                                <VideocamIcon />
                                              </IconButton>
                                              <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() =>
                                                  circuitExerciseFields.remove(
                                                    subIndex
                                                  )
                                                }
                                              >
                                                <ClearIcon />
                                              </IconButton>
                                            </Box>
                                            <Field
                                              name={`${subName}.video`}
                                              subscription={{ value: true }}
                                              render={({ input }) => {
                                                const circuitVideoUrl =
                                                  input.value;
                                                return circuitVideoUrl ? (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                      mb: 1,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                    >
                                                      Video:{" "}
                                                      {circuitVideoUrl
                                                        .split("/")
                                                        .pop()}
                                                    </Typography>
                                                    <IconButton
                                                      color="error"
                                                      aria-label="remove video"
                                                      onClick={() => {
                                                        input.onChange(""); // Clear the video field
                                                      }}
                                                      size="small"
                                                    >
                                                      <ClearIcon />
                                                    </IconButton>
                                                  </Box>
                                                ) : null;
                                              }}
                                            />
                                            <Field
                                              name={`${subName}.fields`}
                                              subscription={{ value: true }}
                                              render={({
                                                input: fieldsInput,
                                              }) => {
                                                const cf =
                                                  Array.isArray(
                                                    fieldsInput.value
                                                  ) &&
                                                  fieldsInput.value.length > 0
                                                    ? fieldsInput.value.slice(
                                                        0,
                                                        2
                                                      )
                                                    : ["RPE", "WEIGHT"];
                                                return (
                                                  <FieldArray
                                                    name={`${subName}.sets`}
                                                    render={({
                                                      fields: subSetFields,
                                                    }) => (
                                                      <>
                                                        <Box
                                                          sx={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            gap: 1,
                                                            mb: 1,
                                                          }}
                                                        >
                                                          <Typography variant="caption">
                                                            Fields:
                                                          </Typography>
                                                          <Field
                                                            name={`${subName}.fields`}
                                                            render={({
                                                              input,
                                                            }) => (
                                                              <Select
                                                                multiple
                                                                {...input}
                                                                size="small"
                                                                value={
                                                                  input.value ||
                                                                  cf
                                                                }
                                                                sx={{
                                                                  minWidth: 140,
                                                                }}
                                                                onChange={(
                                                                  e
                                                                ) => {
                                                                  const next =
                                                                    Array.from(
                                                                      new Set(
                                                                        e.target.value
                                                                      )
                                                                    ).slice(
                                                                      0,
                                                                      2
                                                                    );
                                                                  input.onChange(
                                                                    next
                                                                  );
                                                                }}
                                                                renderValue={(
                                                                  vals
                                                                ) =>
                                                                  (
                                                                    vals || []
                                                                  ).join(", ")
                                                                }
                                                              >
                                                                {FIELD_OPTIONS.map((o) => (
                                                                  <MenuItem
                                                                    key={o}
                                                                    value={o}
                                                                    disabled={
                                                                      !(
                                                                        input.value ||
                                                                        cf
                                                                      ).includes(
                                                                        o
                                                                      ) &&
                                                                      (
                                                                        input.value ||
                                                                        cf
                                                                      )
                                                                        .length >=
                                                                        2
                                                                    }
                                                                  >
                                                                    {o}
                                                                  </MenuItem>
                                                                ))}
                                                              </Select>
                                                            )}
                                                          />
                                                          {cf.includes("DISTANCE") && (
                                                            <Field
                                                              name={`${subName}.distanceUnit`}
                                                              subscription={{ value: true }}
                                                              render={({ input: unitInput }) => (
                                                                <Select
                                                                  {...unitInput}
                                                                  size="small"
                                                                  value={unitInput.value || "m"}
                                                                  sx={{ minWidth: 80 }}
                                                                  onChange={(e) => unitInput.onChange(e.target.value)}
                                                                >
                                                                  {DISTANCE_UNITS.map((u) => (
                                                                    <MenuItem key={u.value} value={u.value}>
                                                                      {u.label}
                                                                    </MenuItem>
                                                                  ))}
                                                                </Select>
                                                              )}
                                                            />
                                                          )}
                                                        </Box>
                                                        {subSetFields.map(
                                                          (setPath) => (
                                                            <Table
                                                              key={setPath}
                                                              sx={{ mt: 1 }}
                                                            >
                                                              <TableHead>
                                                                <TableRow>
                                                                  <TableCell>
                                                                    Reps (each)
                                                                  </TableCell>
                                                                  {cf.includes(
                                                                    "WEIGHT"
                                                                  ) && (
                                                                    <TableCell>
                                                                      Weight
                                                                      (each)
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "RPE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      RPE
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "TIME"
                                                                  ) && (
                                                                    <TableCell>
                                                                      Time (sec)
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "DISTANCE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      Distance
                                                                    </TableCell>
                                                                  )}
                                                                </TableRow>
                                                              </TableHead>
                                                              <TableBody>
                                                                <TableRow>
                                                                  <TableCell>
                                                                    <Box
                                                                      sx={{
                                                                        display:
                                                                          "flex",
                                                                        alignItems:
                                                                          "center",
                                                                        gap: 1,
                                                                      }}
                                                                    >
                                                                      <Field
                                                                        name={`${setPath}.reps`}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <TextField
                                                                            {...input}
                                                                            size="small"
                                                                            sx={{
                                                                              width: 80,
                                                                            }}
                                                                          />
                                                                        )}
                                                                      />
                                                                      <Field
                                                                        name={`${setPath}.repsPerSide`}
                                                                        subscription={{
                                                                          value: true,
                                                                        }}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <FormControlLabel
                                                                            control={
                                                                              <Checkbox
                                                                                size="small"
                                                                                checked={
                                                                                  !!input.value
                                                                                }
                                                                                onChange={(
                                                                                  e
                                                                                ) =>
                                                                                  input.onChange(
                                                                                    e
                                                                                      .target
                                                                                      .checked
                                                                                  )
                                                                                }
                                                                              />
                                                                            }
                                                                            sx={{
                                                                              m: 0,
                                                                            }}
                                                                          />
                                                                        )}
                                                                      />
                                                                    </Box>
                                                                  </TableCell>
                                                                  {cf.includes(
                                                                    "WEIGHT"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Box
                                                                        sx={{
                                                                          display:
                                                                            "flex",
                                                                          alignItems:
                                                                            "center",
                                                                          gap: 1,
                                                                        }}
                                                                      >
                                                                        <Field
                                                                          name={`${setPath}.weight`}
                                                                          render={({
                                                                            input,
                                                                          }) => (
                                                                            <TextField
                                                                              {...input}
                                                                              size="small"
                                                                              sx={{
                                                                                width: 80,
                                                                              }}
                                                                            />
                                                                          )}
                                                                        />
                                                                        <Field
                                                                          name={`${setPath}.weightPerSide`}
                                                                          subscription={{
                                                                            value: true,
                                                                          }}
                                                                          render={({
                                                                            input,
                                                                          }) => (
                                                                            <FormControlLabel
                                                                              control={
                                                                                <Checkbox
                                                                                  size="small"
                                                                                  checked={
                                                                                    !!input.value
                                                                                  }
                                                                                  onChange={(
                                                                                    e
                                                                                  ) =>
                                                                                    input.onChange(
                                                                                      e
                                                                                        .target
                                                                                        .checked
                                                                                    )
                                                                                  }
                                                                                />
                                                                              }
                                                                              sx={{
                                                                                m: 0,
                                                                              }}
                                                                            />
                                                                          )}
                                                                        />
                                                                      </Box>
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "RPE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Field
                                                                        name={`${setPath}.rpe`}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <FormControl size="small">
                                                                            <Select
                                                                              {...input}
                                                                              sx={{
                                                                                width: 120,
                                                                              }}
                                                                            >
                                                                              <MenuItem value="6">
                                                                                6
                                                                              </MenuItem>
                                                                              <MenuItem value="7">
                                                                                7
                                                                              </MenuItem>
                                                                              <MenuItem value="8">
                                                                                8
                                                                              </MenuItem>
                                                                              <MenuItem value="9">
                                                                                9
                                                                              </MenuItem>
                                                                              <MenuItem value="10">
                                                                                10
                                                                              </MenuItem>
                                                                            </Select>
                                                                          </FormControl>
                                                                        )}
                                                                      />
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "TIME"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Field
                                                                        name={`${setPath}.time`}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <TextField
                                                                            {...input}
                                                                            size="small"
                                                                            sx={{
                                                                              width: 120,
                                                                            }}
                                                                            type="number"
                                                                            inputProps={{
                                                                              min: 0,
                                                                            }}
                                                                          />
                                                                        )}
                                                                      />
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "DISTANCE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Field
                                                                        name={`${setPath}.distance`}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <TextField
                                                                            {...input}
                                                                            size="small"
                                                                            sx={{
                                                                              width: 100,
                                                                            }}
                                                                            type="number"
                                                                            inputProps={{
                                                                              min: 0,
                                                                              step: "any",
                                                                            }}
                                                                          />
                                                                        )}
                                                                      />
                                                                    </TableCell>
                                                                  )}
                                                                </TableRow>
                                                              </TableBody>
                                                            </Table>
                                                          )
                                                        )}
                                                      </>
                                                    )}
                                                  />
                                                );
                                              }}
                                            />
                                          </Box>
                                        )
                                      )}
                                    </>
                                  );
                                }}
                              />
                            </CardContent>
                          </Card>
                        ))}
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={handleAddRound}
                            disabled={roundFields.length >= 10}
                            size="small"
                          >
                            Add Round
                          </Button>
                        </Box>
                      </Box>
                    );
                  }}
                />
              )}
            </>
          );
        }}
      />
    </Box>
  );
};

const SetActions = ({
  exerciseIndex,
  setIndex,
  onRemove,
  fields,
  activeFields,
}) => {
  const form = useForm();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const handleWeightChange = (value) => {
    // Update current set's weight
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].weight`, value);

    // Update all sets below with the same weight
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].weight`, value);
    }
  };

  const handleWeightPerHandChange = (value) => {
    form.change(
      `exercises[${exerciseIndex}].sets[${setIndex}].weightPerSide`,
      value
    );
    // Update all sets below with the same weight per hand
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(
        `exercises[${exerciseIndex}].sets[${i}].weightPerSide`,
        value
      );
    }
  };

  const handleRepsPerSideChange = (value) => {
    form.change(
      `exercises[${exerciseIndex}].sets[${setIndex}].repsPerSide`,
      value
    );
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].repsPerSide`, value);
    }
  };

  const handleRpeChange = (value) => {
    // Update current set's RPE
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].rpe`, value);

    // Update all sets below with the same RPE
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].rpe`, value);
    }
  };

  const includesWeight = activeFields.includes("WEIGHT");
  const includesRpe = activeFields.includes("RPE");
  const includesTime = activeFields.includes("TIME");
  const includesDistance = activeFields.includes("DISTANCE");

  const handleDistanceChange = (value) => {
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].distance`, value);
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].distance`, value);
    }
  };

  const handleRepsChange = (value) => {
    // Update current set's reps
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].reps`, value);

    // Update all sets below with the same reps
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].reps`, value);
    }
  };

  const handleTimeChange = (value) => {
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].time`, value);

    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].time`, value);
    }
  };

  return (
    <TableRow>
      {!isMobile && (
        <TableCell sx={{ padding: "5px" }}>{setIndex + 1}</TableCell>
      )}
      <TableCell align="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].reps`}
            render={({ input }) => (
              <TextField
                {...input}
                variant="outlined"
                size="small"
                sx={{ width: 60 }}
                onChange={(e) => handleRepsChange(e.target.value)}
              />
            )}
          />
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].repsPerSide`}
            subscription={{ value: true }}
            render={({ input: checkboxInput }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!checkboxInput.value}
                    onChange={(e) => handleRepsPerSideChange(e.target.checked)}
                  />
                }
                sx={{ m: 0 }}
              />
            )}
          />
        </Box>
      </TableCell>
      {includesWeight && (
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Field
              name={`exercises[${exerciseIndex}].sets[${setIndex}].weight`}
              render={({ input }) => (
                <TextField
                  {...input}
                  variant="outlined"
                  size="small"
                  sx={{ width: 80 }}
                  label="Weight"
                  onChange={(e) => handleWeightChange(e.target.value)}
                />
              )}
            />
            <Field
              name={`exercises[${exerciseIndex}].sets[${setIndex}].weightPerSide`}
              subscription={{ value: true }}
              render={({ input: checkboxInput }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!checkboxInput.value}
                      onChange={(e) =>
                        handleWeightPerHandChange(e.target.checked)
                      }
                    />
                  }
                  sx={{ m: 0 }}
                />
              )}
            />
          </Box>
        </TableCell>
      )}
      {includesRpe && (
        <TableCell>
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].rpe`}
            render={({ input }) => (
              <FormControl size="small" sx={{ width: 100 }}>
                <Select
                  {...input}
                  onChange={(e) => handleRpeChange(e.target.value)}
                >
                  {["6", "7", "8", "9", "10"].map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </TableCell>
      )}
      {includesTime && (
        <TableCell>
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].time`}
            render={({ input }) => (
              <TextField
                {...input}
                variant="outlined"
                size="small"
                sx={{ width: 90 }}
                label="Sec"
                type="number"
                inputProps={{ min: 0 }}
                onChange={(e) => handleTimeChange(e.target.value)}
              />
            )}
          />
        </TableCell>
      )}
      {includesDistance && (
        <TableCell>
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].distance`}
            render={({ input }) => (
              <TextField
                {...input}
                variant="outlined"
                size="small"
                sx={{ width: 90 }}
                type="number"
                inputProps={{ min: 0, step: "any" }}
                onChange={(e) => handleDistanceChange(e.target.value)}
              />
            )}
          />
        </TableCell>
      )}
      <TableCell>
        <ClearIcon
          color={"warning"}
          onClick={onRemove}
          sx={{ cursor: "pointer" }}
        />
      </TableCell>
    </TableRow>
  );
};

export default AddExercise;
